// Drift: medlemskaber og medlemshistorik på Supabase (del af SupabaseApi – se supabase.ts). Hver liste hentes i ét kald.
// Historikken (livstidsværdi, betalinger og frafald) regnes i databasen ud fra Frisbii-arkivet (frisbii_historik_tal og _side).
import type { Api, BesoegKort, MedlemskabVisning, MuligtMedlem } from './api';
import type { Bil, Kunde, Medlemskab } from '../../core/typer';
import type { HistorikSide, HistorikTal } from '../../core/medlemsHistorik';
import type { MedlemsOverblikData } from '../../core/medlemsOverblik';
import { fejl } from '../../core/fejl';
import { rensTekst } from '../../core/validering';
import type { SbKontekst } from './sbKatalog';

type R = Record<string, unknown>;

/** Databasens medlemskab (medlemskaber_oversigt / mine_data) → MedlemskabVisning. Kort og træk kommer med Viva. */
export const tilMedlemskab = (m: R): MedlemskabVisning => ({
  ...(m as unknown as Medlemskab), bil_id: String(m.bil_id ?? ''), note: String(m.note ?? ''), tilvalg: (m.tilvalg as Medlemskab['tilvalg']) ?? [],
  kunde: m.kunde as Kunde, bil: (m.bil as Bil | null) ?? { id: '', kunde_id: String(m.kunde_id), maerke_model: '', nummerplade: '', biltype_id: '' },
  traek: [], kortTilmeldt: false, forrige: (m.forrige as BesoegKort | null) ?? null, naeste: (m.naeste as BesoegKort | null) ?? null,
});

export class SbMedlemmer {
  constructor(private readonly c: SbKontekst) {}

  /** Alle medlemskaber med kunde, bil og forrige/næste besøg – i ét kald (medlemskaber_oversigt). Kort og træk kommer med Viva. */
  medlemskaber: Api['medlemskaber'] = async () => this.forKunde(null);
  /** Én kundes medlemskaber (kundekortet) – eller alle (null). */
  async forKunde(kunde: string | null): Promise<MedlemskabVisning[]> {
    const l = (await this.c.k(this.c.sb.rpc('medlemskaber_oversigt', { k: await this.c.kraevKlient(), p_kunde: kunde }))) as R[] | null;
    return (l ?? []).map(tilMedlemskab);
  }

  /** "Tjek manuelt: mulige medlemmer fra LatePoint" (lp_mulige_medlemmer). */
  muligeMedlemmer: Api['muligeMedlemmer'] = async () =>
    ((await this.c.k(this.c.sb.rpc('lp_mulige_medlemmer', { k: await this.c.kraevKlient() }))) as MuligtMedlem[] | null) ?? [];
  muligtMedlemTjekket: Api['muligtMedlemTjekket'] = async (bil_id) => {
    await this.c.k(this.c.sb.rpc('lp_mulig_medlem_tjekket', { k: await this.c.kraevKlient(), p_bil: bil_id }));
    this.c.skift();
  };

  /** Historik fra Frisbii: nøgletal og måneder regnes i databasen (frisbii_historik_tal). null = intet arkiv for virksomheden. */
  medlemsHistorikTal: Api['medlemsHistorikTal'] = async () => {
    const h = (await this.c.k(this.c.sb.rpc('frisbii_historik_tal', { k: await this.c.kraevKlient() }))) as (HistorikTal & { har: boolean }) | null;
    return h?.har ? { noegletal: h.noegletal, maaneder: h.maaneder } : null;
  };
  /** Kunderne i historikken, 50 ad gangen – søg, status og sortering i databasen (frisbii_historik_side). */
  medlemsHistorikKunder: Api['medlemsHistorikKunder'] = async (f) =>
    (await this.c.k(this.c.sb.rpc('frisbii_historik_side', { k: await this.c.kraevKlient(), f: { ...f, antal: f.antal ?? null } }))) as HistorikSide;
  /** Pause, genoptag, opsig (aflyser kommende besøg) eller ret pris, behandling og navn. */
  retMedlemskab: Api['retMedlemskab'] = async (id, r) => {
    await this.c.k(this.c.sb.rpc('ret_medlemskab', { k: await this.c.kraevKlient(), p_id: id, r }));
    this.c.skift();
  };
  /** Medlemsoverblikket: medlemskab, minimum, besøg, kundens omsætning og historik (medlemskab_overblik). */
  medlemskabOverblik: Api['medlemskabOverblik'] = async (id) => (await this.c.k(this.c.sb.rpc('medlemskab_overblik', { k: await this.c.kraevKlient(), p_id: id }))) as MedlemsOverblikData;
  /** Opsig med det samme: kommende besøg aflyses stille, evt. efterregning (minimum ikke nået) eller frafald med begrundelse. */
  opsigMedlemskab: Api['opsigMedlemskab'] = async (id, p) => {
    if (p.efterregning === 'frafald' && rensTekst(p.frafald_grund ?? '', 300).trim().length < 3) throw fejl('Skriv hvorfor efterregningen frafaldes.', { frafald_grund: 'Skriv en begrundelse.' });
    const r = (await this.c.k(this.c.sb.rpc('opsig_medlemskab', { k: await this.c.kraevKlient(), p_id: id, p }))) as { aflyst: number; efterregning: number; efterregning_booking: number | null };
    this.c.skift();
    return r;
  };
}
